import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import {
  SparklesIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  UserGroupIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';

type ConnectionStatus = {
  connected: boolean;
  user?: string;
  account?: string;
  accountId?: string;
  currency?: string;
  timezone?: string;
};

type Campaign = {
  id?: string;
  name: string;
  status: string;
  objective: string;
  daily_budget: number;
  effective_status: string;
  created_time: string;
};

type Insights = {
  spend: number;
  impressions: number;
  clicks: number;
  reach: number;
  ctr: number;
  cpc: number;
  cpm: number;
  actions?: any[];
};

type AnalysisResult = {
  analysis: string;
  rawData?: any;
  analyzedAt?: string;
};

type Currency = 'ARS' | 'USD';

const DATE_PRESETS = [
  { value: 'last_7d', label: 'Ultimos 7 dias' },
  { value: 'last_14d', label: 'Ultimos 14 dias' },
  { value: 'last_30d', label: 'Ultimos 30 dias' },
  { value: 'last_90d', label: 'Ultimos 90 dias' },
];

const formatCurrencyValue = (value: number, currency: Currency, exchangeRate: number): string => {
  if (currency === 'USD') {
    const usdValue = exchangeRate > 0 ? value / exchangeRate : 0;
    return `US$ ${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(usdValue)}`;
  }
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

const formatNumber = (value: number): string => new Intl.NumberFormat('es-AR').format(Math.round(value));
const formatPercent = (value: number): string => `${Number(value).toFixed(2)}%`;

const Marketing: React.FC = () => {
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [insights, setInsights] = useState<Insights | null>(null);
  const [datePreset, setDatePreset] = useState('last_30d');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [togglingCampaign, setTogglingCampaign] = useState<string | null>(null);
  const [currency, setCurrency] = useState<Currency>('ARS');
  const [exchangeRate, setExchangeRate] = useState<number>(0);

  const fetchExchangeRate = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/dashboard/exchange-rates');
      if (data?.blue?.sell) setExchangeRate(data.blue.sell);
    } catch { /* silenciar */ }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/marketing/status');
      setStatus(data);
      return data.connected;
    } catch {
      setStatus({ connected: false });
      return false;
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [campaignsRes, insightsRes] = await Promise.all([
        axios.get('/api/marketing/campaigns'),
        axios.get(`/api/marketing/insights?datePreset=${datePreset}`),
      ]);
      setCampaigns(campaignsRes.data.campaigns || []);
      const ins = insightsRes.data.insights;
      if (Array.isArray(ins) && ins.length > 0) setInsights(ins[0]);
      else if (ins && !Array.isArray(ins)) setInsights(ins);
      else setInsights(null);
    } catch (err) {
      console.error('Error fetching marketing data:', err);
    }
  }, [datePreset]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setLoading(true);
      const [connected] = await Promise.all([fetchStatus(), fetchExchangeRate()]);
      if (connected && !cancelled) await fetchData();
      if (!cancelled) setLoading(false);
    };
    init();
    return () => { cancelled = true; };
  }, [fetchStatus, fetchData, fetchExchangeRate]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await axios.post('/api/marketing/analyze', { datePreset });
      setAnalysis(data);
      toast.success('Analisis completado');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Error al analizar');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleToggleCampaign = async (campaignId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setTogglingCampaign(campaignId);
    try {
      await axios.post(`/api/marketing/campaigns/${campaignId}/status`, { status: newStatus });
      setCampaigns((prev) =>
        prev.map((c) => c.id === campaignId ? { ...c, effective_status: newStatus, status: newStatus } : c)
      );
      toast.success(`Campana ${newStatus === 'ACTIVE' ? 'activada' : 'pausada'}`);
    } catch {
      toast.error('Error al cambiar estado de campana');
    } finally {
      setTogglingCampaign(null);
    }
  };

  const fmtCur = (value: number) => formatCurrencyValue(value, currency, exchangeRate);

  const getStatusBadge = (effectiveStatus: string) => {
    switch (effectiveStatus) {
      case 'ACTIVE':
        return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">Activa</Badge>;
      case 'PAUSED':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">Pausada</Badge>;
      case 'DELETED':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Eliminada</Badge>;
      default:
        return <Badge variant="secondary">{effectiveStatus}</Badge>;
    }
  };

  const renderAnalysis = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('###')) return <h3 key={i} className="text-lg font-semibold mt-4 mb-2 text-foreground">{line.replace(/^###\s*/, '')}</h3>;
      if (line.startsWith('##')) return <h2 key={i} className="text-xl font-bold mt-5 mb-2 text-foreground">{line.replace(/^##\s*/, '')}</h2>;
      if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="font-semibold mt-3 mb-1 text-foreground">{line.replace(/\*\*/g, '')}</p>;
      if (line.startsWith('- ') || line.startsWith('* ')) {
        const content = line.replace(/^[-*]\s/, '');
        const boldParts = content.split(/\*\*(.*?)\*\*/g);
        return <li key={i} className="ml-4 text-muted-foreground list-disc">{boldParts.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-foreground">{part}</strong> : part)}</li>;
      }
      if (line.trim() === '') return <br key={i} />;
      const boldParts = line.split(/\*\*(.*?)\*\*/g);
      return <p key={i} className="text-muted-foreground mb-1">{boldParts.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-foreground">{part}</strong> : part)}</p>;
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-40 rounded bg-muted animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, idx) => (
            <div key={idx} className="h-24 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header - mismo estilo que Dashboard */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-semibold text-foreground leading-tight">Marketing</h1>
          {status?.connected ? (
            <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">Conectado</Badge>
          ) : (
            <Badge variant="secondary" className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Desconectado</Badge>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {/* Currency Toggle - estilo pill como Dashboard period selector */}
          <div className="flex rounded-full border border-border overflow-hidden">
            <button
              onClick={() => setCurrency('ARS')}
              className={`h-8 px-4 text-sm font-medium transition-colors ${
                currency === 'ARS'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              ARS
            </button>
            <button
              onClick={() => setCurrency('USD')}
              disabled={exchangeRate <= 0}
              className={`h-8 px-4 text-sm font-medium transition-colors ${
                currency === 'USD'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              USD
            </button>
          </div>
          {/* Date Presets - estilo pill como Dashboard */}
          {DATE_PRESETS.map((preset) => (
            <Button
              key={preset.value}
              variant={datePreset === preset.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDatePreset(preset.value)}
              className="h-8 rounded-full px-4"
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Connection Info */}
      {status?.connected && (
        <section className="space-y-2">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-foreground">Cuenta Meta Ads</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Cuenta</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{status.account || '-'}</p>
            </div>
            <div className="rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Usuario</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{status.user || '-'}</p>
            </div>
            <div className="rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">ID Cuenta</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{status.accountId || '-'}</p>
            </div>
            {currency === 'USD' && exchangeRate > 0 && (
              <div className="rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Dolar Blue</p>
                <div className="mt-1 flex items-end justify-between">
                  <p className="text-lg font-semibold text-foreground">$ {new Intl.NumberFormat('es-AR').format(exchangeRate)}</p>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {!status?.connected && (
        <div className="rounded-lg border border-border bg-card px-4 py-8 shadow-sm text-center">
          <p className="text-muted-foreground">
            No hay una cuenta de Meta Ads conectada. Configura la conexion desde el panel de administracion.
          </p>
        </div>
      )}

      {status?.connected && (
        <>
          {/* KPI Cards - mismo estilo que Dashboard */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-foreground">Metricas</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              {[
                { label: 'Inversion', value: insights ? fmtCur(insights.spend) : '-', icon: CurrencyDollarIcon, color: 'text-blue-500' },
                { label: 'Impresiones', value: insights ? formatNumber(insights.impressions) : '-', icon: EyeIcon, color: 'text-purple-500' },
                { label: 'Clicks', value: insights ? formatNumber(insights.clicks) : '-', icon: CursorArrowRaysIcon, color: 'text-green-500' },
                { label: 'CTR', value: insights ? formatPercent(insights.ctr) : '-', icon: ChartBarIcon, color: 'text-orange-500' },
                { label: 'CPC', value: insights ? fmtCur(insights.cpc) : '-', icon: CurrencyDollarIcon, color: 'text-red-500' },
                { label: 'Alcance', value: insights ? formatNumber(insights.reach) : '-', icon: UserGroupIcon, color: 'text-teal-500' },
              ].map((kpi) => {
                const Icon = kpi.icon;
                return (
                  <div key={kpi.label} className="rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon className={`h-4 w-4 ${kpi.color}`} />
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">{kpi.label}</p>
                    </div>
                    <p className="text-xl font-semibold text-foreground">{kpi.value}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Campaigns Table */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
                <MegaphoneIcon className="h-5 w-5" />
                Campanas ({campaigns.length})
              </h2>
            </div>
            <div className="rounded-lg border border-border bg-card shadow-sm overflow-hidden">
              {campaigns.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">No hay campanas disponibles</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Objetivo</TableHead>
                        <TableHead className="text-right">Presupuesto diario</TableHead>
                        <TableHead>Creada</TableHead>
                        <TableHead className="text-center">Accion</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {campaigns.map((campaign, idx) => {
                        const campaignId = campaign.id || String(idx);
                        const isActive = campaign.effective_status === 'ACTIVE';
                        const canToggle = campaign.effective_status === 'ACTIVE' || campaign.effective_status === 'PAUSED';
                        return (
                          <TableRow key={campaignId}>
                            <TableCell className="font-medium max-w-[200px] truncate">{campaign.name}</TableCell>
                            <TableCell>{getStatusBadge(campaign.effective_status)}</TableCell>
                            <TableCell className="text-muted-foreground capitalize">{campaign.objective?.toLowerCase().replace(/_/g, ' ') || '-'}</TableCell>
                            <TableCell className="text-right">{campaign.daily_budget ? fmtCur(campaign.daily_budget / 100) : '-'}</TableCell>
                            <TableCell className="text-muted-foreground">{campaign.created_time ? new Date(campaign.created_time).toLocaleDateString('es-AR') : '-'}</TableCell>
                            <TableCell className="text-center">
                              {canToggle && (
                                <Switch
                                  checked={isActive}
                                  disabled={togglingCampaign === campaignId}
                                  onCheckedChange={() => handleToggleCampaign(campaignId, campaign.effective_status)}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </section>

          {/* AI Analysis Section */}
          <section className="space-y-2">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-amber-500" />
                Analisis con Inteligencia Artificial
              </h2>
              <Button
                onClick={handleAnalyze}
                disabled={analyzing}
                variant="outline"
                className="h-8 rounded-lg gap-1.5 px-3 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20"
              >
                {analyzing ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-purple-500 border-t-transparent" />
                    Analizando...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-4 w-4" />
                    Analizar con IA
                  </>
                )}
              </Button>
            </div>
            <div className="rounded-lg border border-border bg-card shadow-sm p-4">
              {analysis ? (
                <>
                  {analysis.analyzedAt && (
                    <p className="text-xs text-muted-foreground mb-3">
                      Analizado: {new Date(analysis.analyzedAt).toLocaleString('es-AR')}
                    </p>
                  )}
                  <div className="max-w-none">
                    {renderAnalysis(analysis.analysis)}
                  </div>
                </>
              ) : !analyzing ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  Hace click en "Analizar con IA" para obtener un analisis detallado de tus campanas y metricas de marketing.
                </p>
              ) : null}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Marketing;
